<template>
  <b-modal
    :modal-class="'search-modal'"
    size="xl"
    :visible="visible"
    :no-fade="true"
    :hide-footer="true"
    @hide="hide()"
    @shown="onShown()"
  >
    <div slot="modal-header" class="w-100">
      <div class="media h5 font-weight-normal mb-0">
        <div class="media-left ml-md-3 mr-3 align-self-center text-muted">
          <label
            class="mb-0"
            for="search_modal_query">
            <i
              v-if="!searching || !query"
              class="fa fa-fw fa-search"
            ></i>
            <i
              v-if="searching && query"
              class="fa fa-fw fa-circle-o-notch fa-spin"
            ></i>
            <span class="sr-only">Your search query</span>
          </label>
        </div>
        <div class="media-body align-self-center">
          <input
            id="search_modal_query"
            v-model="query"
            type="text"
            class="form-control border-0"
            :placeholder="placeholder"
            @keydown="keydown"
          >
        </div>
        <div class="media-right align-self-center">
          <button
            class="btn btn-lg btn-link text-body text-decoration-none pr-1"
            @click.prevent="hide()"
          >
            <i class="fa fa-close"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="text-muted px-md-3 mb-1" v-if="!searching && empty === true">
      {{ noMatches }}
    </div>

    <div
      v-if="count > 0"
    >
      <section
        v-for="(list, category) in resourcesByCategory"
        :key="`category:${category}`"
        class="mb-3"
      >
        <h3
          class="h6 text-uppercase text-muted small"
          v-text="category"
        ></h3>
        <ul
          class="list-unstyled mb-0"
        >
          <li
            v-for="resource in list"
            :key="`resource:${resource.id}`"
          >
            <a href="#" @click.prevent="hide() && route(resource)" :class="{ 'text-decoration-none media p-2 rounded': true, 'bg-primary': resource === selected }">
              <span :class="{ 'media-left align-self-center mr-1': true, 'text-white': resource === selected }">
                <i
                  v-if="!isImageIcon(resource)"
                  :class="`text-body fa fa-fw ${icon(resource)} mr-2`"
                ></i>
                <span
                  v-if="isImageIcon(resource)"
                  class="agent-profile-img tiny mr-2 position-relative"
                  :style="`top:3px; background-image: url(${icon(resource)});`"
                ></span>
              </span>
              <span :class="{ 'media-body align-self-center': true, 'text-body': resource !== selected, 'text-white': resource === selected }" style="overflow-x:hidden">
                <span class="text-nowrap">
                  {{ resource.title}}
                  <span :class="{ 'text-muted': resource !== selected, 'text-white': resource === selected }" v-if="resource.subtitle">
                    - {{ resource.subtitle }}
                  </span>
                </span>
              </span>
            </a>
          </li>
        </ul>
      </section>
    </div>

  </b-modal>
</template>

<style lang="scss">
.search-modal {
  .modal-body {
    height: 60vh;
    overflow: scroll;
  }

  @media screen and (max-width: 768px) {
    .modal-content {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 0;
    }

    .modal-body {
      position: fixed;
      top: 81px;
      height: calc(100vh - 81px);
      left: 0;
      right: 0;
      bottom: 0;
      overflow: scroll;
      -webkit-overflow-scrolling: touch;
    }
  }
}
</style>

<script>
import $ from 'jquery'
import alertify from 'alertify.js'
import _ from 'lodash'
import { search } from '@/api'

// import elasticlunr from 'elasticlunr'

const $window = $(window)

export default {
  props: {
    placeholder: {
      type: String,
      default: 'Search for anything...'
    },
    noMatches: {
      type: String,
      default: 'No matches.'
    },
    route: {
      type: Function,
      default: (resource) => {
        if (resource.link || get(resource, 'links.view')) {
          document.location = resource.link || get(resource, 'links.view')
        } else {
          alertify.error("Can't link to that.")
        }
      }
    }
  },
  data () {
    return {
      empty: false,
      visible: false,
      selectedIndex: 0,
      searching: false,
      query: '',
      resources: [],
      resourcesByCategory: {},
      count: null,
      categories: {},
      push: []
    }
  },
  mounted () {
    $(document).keydown((e) => {
      if (this.isSearchKey(e)) {
        this.toggle()
        e.preventDefault()
      }
    })

    $window.on('search.requested', (e) => {
      this.show()
    })

    $('[data-trigger="search"]').click((e) => {
      this.toggle()
      e.preventDefault()
    })
  },
  watch: {
    query: _.debounce(function () {
      this.search()
    }, 200),
    resourcesByCategory: {
      deep: true
    }
  },
  computed: {
    selectedResource () {
      if (this.selectedIndex > -1 && this.count > 0 && this.resources[this.selectedIndex]) {
        return this.resources[this.selectedIndex]
      } else {
        return false
      }
    },
    selected () {
      return this.resources[this.selectedIndex]
    }
  },
  methods: {
    toggle () {
      if (this.visible) {
        this.hide()
      } else {
        this.show()
      }
    },
    isSearchKey (e) {
      if ((e.key || '').toUpperCase() === 'J') {
        if (this.isMacOS()) {
          return e.metaKey
        } else {
          return e.ctrlKey
        }
      }
    },
    isMacOS () {
      return navigator.userAgent.indexOf('Mac OS') !== -1
    },
    async search () {
      if (this.query.length > 0) {
        this.searching = true
        try {
          let response = await search(this.query)
          this.setResources(response.data)
        } catch (error) {
          alertify.error("Oops! We failed to run your search. Please try again.")
        } finally {
          this.searching = false
        }
      }
    },
    setResources (resources) {
      this.count = resources.length

      this.resourcesByCategory = {}
      this.resources = []
      for(let i in resources) {
        let resource = resources[i]
        if (!this.resourcesByCategory[resource.category]) {
          this.resourcesByCategory[resource.category] = [resource]
        } else {
          this.resourcesByCategory[resource.category].push(resource)
        }
        this.resources.push(resource)
      }
      this.selectedIndex = 0
      this.empty = this.query.length > 0 && this.count < 1
    },
    show () {
      this.visible = true
    },
    hide () {
      this.query = ''
      this.selectedIndex = 0
      this.visible = false
      return true
    },
    hideAfterDelay: _.debounce(function () {
      this.hide()
    }, 150),
    icon (resource) {
      return resource.icon || 'fa-link'
    },
    isImageIcon (resource) {
      return this.icon(resource).indexOf('http') === 0
    },
    keydown (e) {
      if (e.key === 'ArrowUp') {
        this.selectedIndex = Math.max(0, this.selectedIndex - 1)
        e.preventDefault()
      } else if (e.key === 'ArrowDown') {
        this.selectedIndex = Math.min(this.count - 1, this.selectedIndex + 1)
        e.preventDefault()
      } else if (e.key === 'Enter') {
        if (this.selectedResource) {
          this.route(this.selectedResource)
          this.visible = false
          e.preventDefault()
        }
      } else if (e.key === 'Escape') {
        this.hide()
        e.preventDefault()
      }
    },
    onShown () {
      this.selectedIndex = 0
      this.$nextTick(() => {
        setTimeout(() => {
          if ($window.width() >= 767) {
            $('#search_modal_query').focus()
          }
        }, 100)
      })
    }
  }
}
</script>